import React, { useEffect, useRef } from 'react';

import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useStickyHeader } from '~/shared/utils/StickyHeaderContext';

import Breadcrumbs from '../components/Breadcrumbs';
import CategoryTabs from '../components/CategoryTabs';
import DiscoveryGuide from '../components/DiscoveryGuide';
import FilterBar from '../components/Filters/FilterBar';
import FilterBarSkeleton from '../components/Filters/FilterBarSkeleton';
import GridScrollHeader from '../components/GridScrollHeader';
import GridSpotlights from '../components/GridSpotlights';
import NumResults from '../components/NumResults';
import VirtualScrollGrid from '../components/VirtualScrollGrid';
import { FiltersPanelProvider } from '../context/FiltersPanelContext';
import {
  useGridFacets,
  useGridMeta,
  useGridProducts,
} from '../context/GridDataContexts';
import GridLayoutTrackingProvider from '../context/GridLayoutTrackingProvider';
import { useGridTabsPortal } from '../context/GridTabsPortalContext';
import { PendingFiltersProvider } from '../context/PendingFiltersContext';
import { useAutoFilters } from '../hooks/useAutofilters';
import useGridInputs from '../hooks/useGridInputs';
import useInGridAssets from '../hooks/useInGridAssets';
import useTabsAndBreadcrumbs from '../hooks/useTabsAndBreadcrumbs';
import { getGridLayoutTrackingTags } from '../utils/tracking';

const FiltersAndNumResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 16px 24px;
  gap: 32px;
`;

const GridViewContainer = styled.div`
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};
  max-width: 1920px;
  width: 100%;
  margin: 0 auto;
`;

// Mirrors GridViewContainer width/centering so the portaled breadcrumbs +
// tabs row reads identically to its inline original — the portal moves the
// DOM parent, not the visual constraints.
const PortaledTabsContainer = styled.div`
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};
  max-width: 1920px;
  width: 100%;
  margin: 0 auto;
`;

const BreadcrumbsWrapper = styled.div<{ $hasTabs: boolean }>`
  margin: 20px 24px;
  ${({ $hasTabs }) => $hasTabs && 'margin-bottom: 16px;'}
`;

const BorderLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) =>
    theme.colors.gridV2CategoryTabContainerBorder};
`;

const DesktopGridView = () => {
  const { setAutoHide } = useStickyHeader() as {
    setAutoHide: React.Dispatch<React.SetStateAction<boolean>>;
  };
  const breadcrumbsRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const numResultsRef = useRef<HTMLDivElement>(null);
  const { tabs } = useTabsAndBreadcrumbs();
  const hasTabs = Boolean(tabs?.length);
  const tabsPortalTarget = useGridTabsPortal();

  // Enable StickyHeader auto-hide while this view is mounted so the main
  // header can collapse/expand with scroll and avoid conflicting states.
  useEffect(() => {
    setAutoHide(true);
    return () => setAutoHide(false);
  }, [setAutoHide]);

  const {
    products,
    fetchNextPage,
    hasNextPage,
    isPrimaryProductsFetching,
    isPrimaryProductsLoading,
    isRelatedProductsFetching,
    isProductPricingFetching,
  } = useGridProducts();

  const { facets, totalNumResults } = useGridFacets();

  const { cioFilterName, cioFilterValue } = useGridMeta();

  const { mergedAssetsAndProducts } = useInGridAssets({ products });
  const { builderState, gridGender, gridType } = useGridInputs();

  const { autofilterProcessed } = useAutoFilters({
    builderState,
    gridGender,
    facets,
    isPrimaryProductsLoading,
  });

  const { trackingTags } = getGridLayoutTrackingTags({
    totalNumResults,
    cioFilterName,
    cioFilterValue,
    gridType,
  });

  // Breadcrumbs + Tabs are portaled to a slot at the top of the page body so
  // they always render above any Builder content (banner / Vimeo / etc.) that
  // an author drops above the grid block. Refs still attach to the actual
  // mounted DOM nodes — `GridScrollHeader`'s `useIsHiddenBehind` observer
  // reads live bounding rects so it tracks the new physical position.
  //
  // We render `null` (not an inline fallback) until the portal target attaches
  // so the React element type in this slot is stable — switching from inline
  // JSX to a portal element across renders would unmount/remount Breadcrumbs +
  // CategoryTabs and trigger a one-frame layout jump from the inline position
  // to the portaled one. The provider attaches its target synchronously inside
  // React's commit phase, so the empty render lasts < 1 paint.
  const breadcrumbsAndTabs: any = tabsPortalTarget
    ? createPortal(
        <PortaledTabsContainer>
          <BreadcrumbsWrapper ref={breadcrumbsRef} $hasTabs={hasTabs}>
            <Breadcrumbs />
          </BreadcrumbsWrapper>
          {hasTabs ? <CategoryTabs /> : <BorderLine />}
        </PortaledTabsContainer>,
        tabsPortalTarget
      )
    : null;

  return (
    <FiltersPanelProvider>
      <GridLayoutTrackingProvider>
        <GridScrollHeader
          breadcrumbsRef={breadcrumbsRef}
          filterBarRef={filterBarRef}
          numResultsRef={numResultsRef}
          totalNumResults={totalNumResults}
        />
        {breadcrumbsAndTabs}
        <GridViewContainer {...trackingTags}>
          <GridSpotlights />
          <DiscoveryGuide />
          <FiltersAndNumResultsContainer ref={filterBarRef}>
            {autofilterProcessed ? (
              <PendingFiltersProvider>
                <FilterBar />
              </PendingFiltersProvider>
            ) : (
              <FilterBarSkeleton />
            )}
            <div ref={numResultsRef}>
              <NumResults
                totalNumResults={totalNumResults}
                isLoading={isPrimaryProductsLoading}
              />
            </div>
          </FiltersAndNumResultsContainer>
          <VirtualScrollGrid
            virtualList={mergedAssetsAndProducts}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isPrimaryProductsLoading={isPrimaryProductsLoading}
            isPrimaryProductsFetching={isPrimaryProductsFetching}
            isRelatedProductsFetching={isRelatedProductsFetching}
            isProductPricingFetching={isProductPricingFetching}
          />
        </GridViewContainer>
      </GridLayoutTrackingProvider>
    </FiltersPanelProvider>
  );
};

export default DesktopGridView;
